/**
 * Block 12 calculator — `hdfQ`: количество листов ХДФ на заднюю стенку.
 *
 * 6 per-anketa variants (Тумба-ванна **исключена** per OQ-7 — ХДФ нет) +
 * top-level dispatcher через discriminated union.
 *
 * Reference: `docs/methodology/calculator-formulas.md` §2.X Б.5 / §4.5.
 */

import type { CalculatorParams } from '../../contracts/calculator';
import { mmToM } from '../utils/mmToM';
import {
  assertPositiveDimension,
  type BalconyParams,
  type BathCabinetParams,
  type BathShelfAboveParams,
  type CabinetParams,
  type KitchenParams,
  type TvZoneParams,
} from './types';

/**
 * Кухня: `hdfQ = Math.ceil(L / 3000)` — каждые 3 м длины = 1 лист ХДФ
 * (per OQ-2 ответ Aibek). Эквивалентно xlsx-ladder для L ≤ 9000, и
 * продолжается для больших значений.
 *
 * **Источник:** xlsx `Кухня!C35`.
 *
 * Reference: `docs/methodology/calculator-formulas.md` §2.1 Б.5 + §4.5.
 *
 * @example
 *   L=3000  → 1   (ceil(1))
 *   L=6000  → 2   (ceil(2))
 *   L=8000  → 3   (ceil(2.667))
 *   L=9000  → 3   (ceil(3))
 *   L=10000 → 4   (ceil(3.333))
 */
export function hdfQKitchen(params: KitchenParams): number {
  assertPositiveDimension(params.length, 'hdfQKitchen.length');
  return Math.ceil(params.length / 3000);
}

/**
 * Шкаф / Гардероб: `hdfQ = Math.ceil(L_м / 2)` (per OQ-4 — применяется
 * **всегда**, включая `isBuiltIn=true`).
 *
 * **Источник:** xlsx `Шкаф!C34` + OQ-4 ответ Aibek.
 *
 * Reference: `docs/methodology/calculator-formulas.md` §2.2 Б.5 + §4.5.
 *
 * @example
 *   L=2000 → 1   (ceil(1))
 *   L=3000 → 2   (ceil(1.5))
 *   L=4000 → 2   (ceil(2))
 *   L=5000 → 3   (ceil(2.5))
 */
export function hdfQCabinet(params: CabinetParams): number {
  assertPositiveDimension(params.length, 'hdfQCabinet.length');
  return Math.ceil(mmToM(params.length) / 2);
}

/**
 * Сан узел шкаф над антресолью: `hdfQ = 1` фиксированно.
 *
 * **Источник:** xlsx `Антресоль!C30`. Антресоль — небольшое изделие,
 * один лист ХДФ всегда достаточен.
 *
 * Reference: `docs/methodology/calculator-formulas.md` §2.4 Б.5 + §4.5.
 */
export function hdfQBathShelfAbove(_params: BathShelfAboveParams): number {
  return 1;
}

/**
 * Сан узел шкаф: `hdfQ = 1` фиксированно.
 *
 * **Источник:** xlsx `Шкаф-санузел!C30`.
 *
 * Reference: `docs/methodology/calculator-formulas.md` §2.5 Б.5 + §4.5.
 */
export function hdfQBathCabinet(_params: BathCabinetParams): number {
  return 1;
}

/**
 * ТВ-зона: `hdfQ = 1` фиксированно.
 *
 * **Источник:** xlsx `ТВ-зона!C32`.
 *
 * Reference: `docs/methodology/calculator-formulas.md` §2.6 Б.5 + §4.5.
 */
export function hdfQTvZone(_params: TvZoneParams): number {
  return 1;
}

/**
 * Балкон: `hdfQ = Math.ceil(L_м / 2)` (прямой) или
 * `Math.ceil((L_м + L2_м − 0.6) / 2)` (угловой, per вопрос B Вариант A).
 *
 * Угловой определяется по наличию `length2` (как в corpusQBalcony).
 *
 * **Источник:** xlsx `Балкон!C17` + вопрос B Aibek (Вариант A).
 *
 * Reference: `docs/methodology/calculator-formulas.md` §2.7 Б.4 + §4.5.
 *
 * @example
 *   L=2000 (прямой) → 1   (ceil(1))
 *   L=2500 (прямой) → 2   (ceil(1.25))
 *   L=3000, L2=2000 (угловой) → 3   (ceil((3+2-0.6)/2) = ceil(2.2) = 3)
 */
export function hdfQBalcony(params: BalconyParams): number {
  assertPositiveDimension(params.length, 'hdfQBalcony.length');
  const isAngular = typeof params.length2 === 'number';
  if (isAngular) {
    assertPositiveDimension(params.length2, 'hdfQBalcony.length2');
    const totalL_m = mmToM(params.length) + mmToM(params.length2) - 0.6;
    if (totalL_m <= 0) {
      throw new Error(
        `hdfQBalcony: angular total length must be > 600mm, got L=${params.length}, L2=${params.length2}`,
      );
    }
    return Math.ceil(totalL_m / 2);
  }
  return Math.ceil(mmToM(params.length) / 2);
}

/**
 * Top-level dispatcher: выбирает вариант формулы по `params.type`.
 *
 * **Не применимо:**
 * - `'bath-tabletop'` — Тумба-ванна **без ХДФ** (OQ-7). throws.
 * - `'other'` — Другая мебель свободный режим. throws.
 *
 * Template engine (Phase B3) должен НЕ вызывать `hdfQ` для этих типов
 * (проверка по `applicableAdditional` / методике перед вычислением).
 */
export function hdfQ(params: CalculatorParams): number {
  switch (params.type) {
    case 'kitchen':
      return hdfQKitchen(params);
    case 'cabinet':
      return hdfQCabinet(params);
    case 'bath-tabletop':
      throw new Error('hdfQ: не применяется к "Тумба-ванна" (нет ХДФ per OQ-7)');
    case 'bath-shelf-above':
      return hdfQBathShelfAbove(params);
    case 'bath-cabinet':
      return hdfQBathCabinet(params);
    case 'tv-zone':
      return hdfQTvZone(params);
    case 'balcony':
      return hdfQBalcony(params);
    case 'other':
      throw new Error('hdfQ: не применяется к "Другая мебель" (свободный режим)');
  }
}
